import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import {
  useArabicSessionLanguage,
  useIsRTL,
  useSessionLanguage,
} from '@context/SessionSettingsContext'

import { FilterOptions } from '@ui/v2/TableFilter/TableFilter'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { prefixFilter } from '@utils/filter.utils'
import { getTransactionDisplay } from '@utils/transaction.utils'

import { TransactionDto } from '@model/TransactionDto'
import { CurrencyType, WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import tableStyles from '@shared/components/TransactionHistoryTable/transactionHistoryTable.module.scss'

import { IBReportResultsPage, useSearch } from '../IBReportResultsPage'
import type { IBReportsTableSortOption } from '../IBReportsTable'
import {
  IBTransactionHistorySortKeys,
  IBTransactionHistoryTable,
} from './IBTransactionHistoryTable'
import { useIBTransactionHistoryFetch } from './hooks/useIBTransactionHistoryFetch'

const useTransactionHistorySearch = (walletId?: string) => {
  const search = useSearch(
    walletId ? { customInitialSearchQuery: { ca_search_WalletId: walletId } } : undefined
  )

  const { t } = useTranslation()
  const isRTL = useIsRTL()
  const sortOptions: IBReportsTableSortOption<IBTransactionHistorySortKeys | null>[] = [
    {
      id: 'TransactionType.name',
      name: t('Type'),
      align: 'left',
      width: 190,
      className: tableStyles.typeCell,
    },
    { id: null, name: t('Amount'), align: 'right', width: 144, className: tableStyles.amountRow },
    { id: null, name: '', width: 32, className: tableStyles.amountSpacerCell },
    {
      id: null,
      name: t('From'),
      align: isRTL ? 'right' : 'left',
      width: 180,
      className: tableStyles.fromToCell,
    },
    { id: null, name: '', align: 'center', width: 60, className: tableStyles.arrowCell },
    {
      id: null,
      name: t('To'),
      align: isRTL ? 'right' : 'left',
      width: 180,
      className: tableStyles.fromToCell,
    },
    {
      id: 'TransactionState.name',
      name: t('Status'),
      align: 'left',
      width: 154,
      className: tableStyles.statusCell,
    },
    {
      id: null,
      name: t('Wallet.Reference number'),
      align: isRTL ? 'right' : 'left',
      className: tableStyles.nameRow,
    },
    { id: null, name: '', width: 40 },
  ]

  return {
    ...search,
    placeholder: t('IB.Search reference'),
    sortOptions,
  }
}

const useDocumentFile = (data: TransactionDto[], walletId?: string) => {
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { account } = useAccountReadContext()

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const isArabic = useArabicSessionLanguage()

  const tableBody = () => {
    const headerCsvData = [
      [
        t('Type'),
        t('Date'),
        t('From'),
        t('To'),
        t('Wallet.Reference number'),
        t('Status'),
        t('Amount'),
      ],
    ]

    return data.reduce((previousValue, x) => {
      const { fields, amountSign } = getTransactionDisplay(x, 'ib', {
        isArabic,
        authId: account?.id,
        walletId,
      })

      return previousValue.concat([
        [
          x.type.name,
          formatDate(x.createdDate, { formatType: dateFormat }),
          fields.from,
          fields.to,
          x.referenceId,
          x.state.name,
          `${walletId ? amountSign : ''}${formatMoney(
            fields.amount,
            fields.currency as CurrencyType
          )}`,
        ],
      ])
    }, headerCsvData)
  }

  return useGenerateDocumentFile('ib_transaction_history_report', tableBody())
}

const useFilterOptions = () => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const clientWalletType = account?.id
    ? prefixFilter({
        prefix: account.id,
        value: WalletTypeEnum.IB.toString(),
      })
    : null

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.ib.transactionHistoryFilterOptions(clientWalletType, locale),
    queryFn: () => apiClient.getTransactionHistoryFilterOptions(clientWalletType!, locale),
    enabled: !!clientWalletType,
  })

  const { data: transactionStates } = useQuery({
    queryKey: queryKeys.ib.transactionStates(locale),
    queryFn: () => apiClient.getTransactionStates(locale),
  })

  return {
    filterOptions: { ...filterOptions, transactionStates },
    clientWalletType,
  }
}

export const IBTransactionHistoryPage: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { walletId } = location.state || {}

  const { filterOptions, clientWalletType } = useFilterOptions()
  const search = useTransactionHistorySearch(walletId)

  const { data, forceRefresh } = useIBTransactionHistoryFetch({
    search,
    clientWalletType,
  })

  const generateDocumentFile = useDocumentFile(data.query.data, walletId)

  const sortOptions = [
    { name: t('Transactions.Transaction Type'), id: 'TransactionType.name' },
    { name: t('Created Date'), id: 'CreatedDate' },
    { name: t('Transactions.Transaction State'), id: 'TransactionState.name' },
  ]

  return (
    <IBReportResultsPage
      title={t('Tabs.Transactions')}
      data={data}
      search={search}
      sortOptions={sortOptions}
      generateDocumentFile={generateDocumentFile}
      scrollToHeaderId={ScrollToIds.IBSignupHeader}
      filterOptions={filterOptions}
      filterOptionsTypes={[
        FilterOptions.TransactionType,
        FilterOptions.TransactionStatus,
        FilterOptions.Date,
        FilterOptions.ClientAmount,
      ]}
      table={
        <IBTransactionHistoryTable
          forceRefresh={forceRefresh}
          data={data}
          search={search}
          walletId={walletId}
        />
      }
    />
  )
}
